import { useState, useMemo, useCallback, useEffect } from 'react'

import clsx from 'clsx'

import {
  PlanOverrideConfirmationDialog,
  SubscriptionUpdateModal,
} from '@buffer-mono/app-shell'
import type { SubscriptionUpdate } from '@buffer-mono/app-shell'
import { CHANNEL_LIMITS } from '@buffer-mono/app-shell/src/components/PlanSelector/constants'
import { useGetCustomInvoice } from '@buffer-mono/app-shell/src/components/PlanSelector/hooks/useGetCustomInvoice'
import { usePlanSelectorQuery } from '@buffer-mono/app-shell/src/components/PlanSelector/hooks/usePlanSelectorQuery'
import { useUpdateBillingSubscriptionPlan } from '@buffer-mono/app-shell/src/components/PlanSelector/hooks/useUpdateBillingSubscriptionPlan'
import {
  BillingIntervalValues,
  OBPlanIdValues,
  UpdateType,
  UpdatePlanPhase,
} from '@buffer-mono/app-shell/src/components/PlanSelector/types'
import {
  getVolumeDiscountMessage,
  findCurrentPlan,
} from '@buffer-mono/app-shell/src/components/PlanSelector/utils/dataTransformers'
import { applyDiscountsToCalculation } from '@buffer-mono/app-shell/src/components/PlanSelector/utils/discountCalculations'
import {
  formatPrice,
  formatPriceSimple,
  calculateTierPrice,
} from '@buffer-mono/app-shell/src/components/PlanSelector/utils/pricingCalculations'
import type {
  ObPlanId,
  BillingInterval as AppShellBillingInterval,
} from '@buffer-mono/app-shell/src/gql/graphql'
import { useScaRetryState } from '@buffer-mono/app-shell/src/hooks/useScaRetryState'
import { useStripePaymentConfirmation } from '@buffer-mono/app-shell/src/hooks/useStripePaymentConfirmation'
import {
  Flex,
  Card,
  UsersIcon,
  AllChannelsIcon,
  CriticalIcon,
  LockOpenIcon,
  Text,
  ShoppingCartIcon,
  Button,
  Heading,
  CounterInput,
  CheckIcon,
  toast,
  Skeleton,
  Tooltip,
  InfoIcon,
  Badge,
  CircleDollarSignIcon,
} from '@buffer-mono/popcorn'

import { graphql, readFragment } from '~publish/graphql'
import { MODALS, useOpenGlobalModal } from '~publish/hooks/useOpenGlobalModal'
import type { FragmentOf, ResultOf } from '~publish/graphql'

import {
  getDiscountCopy,
  OrganizationDataForBilling_Organization,
} from './PlanContent'
import { AddOrRemoveChannelsDialog } from '../../shared/components/AddOrRemoveChannelsDialog'
import { getUpcomingInvoiceCopy } from '../utils/getUpcomingInvoiceCopy'
import { getUsersCopyV2 } from '../utils/helpers'

import styles from './PlanCardsVertical.module.css'

const isObPlanId = (value: string): value is ObPlanId =>
  Object.values(OBPlanIdValues).some((planId) => planId === value)

const isBillingInterval = (value: string): value is AppShellBillingInterval =>
  Object.values(BillingIntervalValues).some((interval) => interval === value)

type InputWidthStyle = React.CSSProperties & { '--input-width': string }

const getInputWidthStyle = (channelCount: number): InputWidthStyle => ({
  '--input-width': `${channelCount.toString().length}ch`,
})

export const PlanCardsVertical_WebBillingDetails = graphql(/* GraphQL */ `
  fragment PlanCardsVertical_WebBillingDetails on WebBillingDetails {
    requiresAssistedBilling
    paymentMethodDetails {
      __typename
    }
    subscription {
      interval
      quantity
      periodEnd
      isCanceledAtPeriodEnd
      trial {
        startDate
        endDate
      }
      plan {
        id
        name
        limits {
          members
        }
      }
      scheduledUpdate {
        newPlan
        newInterval
        newQuantity
        effectiveDate
      }
    }
    discounts {
      __typename
      ... on AmountDiscount {
        amountValue: value
        duration
        durationInMonths
      }
      ... on PercentDiscount {
        percentValue: value
        duration
        durationInMonths
      }
    }
    nextInvoice {
      totalAmount
      taxAmount
      currency
    }
  }
`)

/**
 * A billing subscription's interval (`'month'` | `'year'`), derived from the
 * `subscription { interval }` selection on
 * {@link PlanCardsVertical_WebBillingDetails}.
 */
type BillingInterval = ResultOf<
  typeof PlanCardsVertical_WebBillingDetails
>['subscription']['interval']

interface PlanCardsVerticalProps {
  organizationData: FragmentOf<
    typeof OrganizationDataForBilling_Organization
  > | null
  webBilling: FragmentOf<typeof PlanCardsVertical_WebBillingDetails>
}

const PlanCardsVertical: React.FC<PlanCardsVerticalProps> = ({
  organizationData,
  webBilling: webBillingRef,
}) => {
  const openGlobalModal = useOpenGlobalModal()
  const webBilling = readFragment(
    PlanCardsVertical_WebBillingDetails,
    webBillingRef,
  )
  const organization = readFragment(
    OrganizationDataForBilling_Organization,
    organizationData,
  )

  // Use raw query data to get ALL available plans (not filtered by billing cycle)
  // This ensures we can find both monthly and yearly plans
  const { data: queryData, refetch } = usePlanSelectorQuery()

  // Use findCurrentPlan utility to correctly match plan by ID and interval
  // Note: Must use queryData.subscription.interval, not billing.subscription.interval
  const currentPlan = useMemo(() => {
    if (!queryData?.availablePlans || !queryData?.subscription) {
      return undefined
    }

    try {
      return findCurrentPlan(
        queryData.availablePlans,
        queryData.subscription.plan.id,
        queryData.subscription.interval,
      )
    } catch {
      // findCurrentPlan throws if no match found - return undefined for graceful degradation
      return undefined
    }
  }, [queryData])

  const { nextInvoice } = webBilling
  const isOnFreePlan = webBilling.subscription.plan.id === OBPlanIdValues.free

  // Prefer API currency; fallback to USD
  const currency = nextInvoice?.currency ?? 'USD'

  const { updatePlan, loading: isUpdatingPlan } =
    useUpdateBillingSubscriptionPlan()

  // SCA payment confirmation hook
  const {
    confirmPayment,
    isConfirming,
    isFeatureEnabled: isScaEnabled,
  } = useStripePaymentConfirmation()

  const {
    getCustomInvoice,
    data: customInvoiceData,
    loading: isLoadingDynamicPrices,
  } = useGetCustomInvoice()
  const { dueNow } = customInvoiceData ?? {
    dueNow: 0,
  }

  // WebBillingDetails.paymentMethodDetails is null when no payment method is
  // configured (free plans that never entered card info); otherwise present.
  const hasPaymentDetails = webBilling.paymentMethodDetails !== null
  const subscription = webBilling.subscription

  // Only show scheduled update if it's today or in the future
  const isFutureScheduledUpdate =
    subscription.scheduledUpdate &&
    new Date(subscription.scheduledUpdate.effectiveDate) >=
      new Date(new Date().setHours(0, 0, 0, 0))

  const displayScheduledUpdate = isFutureScheduledUpdate
    ? subscription.scheduledUpdate
    : null

  // The legacy `paymentMethod === 'bank'` enum is replaced by a discriminated
  // union on PaymentMethodDetails.
  const isPayingWithBank =
    webBilling.paymentMethodDetails?.__typename === 'BankPaymentMethodDetails'
  // WebBillingDetails dropped the `trial.isActive` scalar; derive from dates.
  const now = new Date()
  const isOnTrial =
    subscription.trial !== null &&
    new Date(subscription.trial.startDate) <= now &&
    now < new Date(subscription.trial.endDate)
  const currentPlanQuantity = subscription.quantity

  const { hasUpcomingInvoice, upcomingInvoiceAmountRaw } =
    getUpcomingInvoiceCopy(nextInvoice)

  // Channel count and limits
  const connectedChannelsCount = organization?.channels?.length ?? 0
  const lockedChannelsCount =
    organization?.channels?.filter((channel) => channel.isLocked).length ?? 0
  const channelsLimit = organization?.limits?.channels

  const usersCopy = getUsersCopyV2(
    subscription.plan.limits?.members ?? 0,
    organization?.members?.totalCount ?? 0,
  )

  // Manage Channels
  const [channelCount, setChannelCount] = useState(currentPlanQuantity)
  const safeChannelCount =
    typeof channelCount === 'number' && !isNaN(channelCount)
      ? channelCount
      : connectedChannelsCount
  const [manageChannelsExpanded, setManageChannelsExpanded] = useState(false)
  const [showOverrideDialog, setShowOverrideDialog] = useState(false)
  const [showSuccessModal, setShowSuccessModal] = useState(false)
  const [subscriptionUpdateData, setSubscriptionUpdateData] =
    useState<SubscriptionUpdate | null>(null)
  const [updatePlanPhase, setUpdatePlanPhase] = useState<UpdatePlanPhase>(
    UpdatePlanPhase.Idle,
  )
  const [showAddOrRemoveChannelsDialog, setShowAddOrRemoveChannelsDialog] =
    useState(false)

  // Payment retry state using centralized hook (VLG-394)
  const {
    retryState,
    saveRetryData,
    clearRetryData,
    hasParametersChanged,
    canRetry,
  } = useScaRetryState<ObPlanId, BillingInterval>()

  // Sync the CounterInput default value when the subscription plan changes
  // This ensures the input resets to the new plan quantity after successful updates
  useEffect(() => {
    setChannelCount(currentPlanQuantity)
  }, [currentPlanQuantity])

  const hasChannelCountChanged = safeChannelCount !== currentPlanQuantity
  const isDowngradingAction = customInvoiceData?.isScheduledChange ?? false

  // Agency plan detection - agency plans require special handling (10 channel minimum)
  const isAgencyPlan = subscription.plan.id === OBPlanIdValues.agency

  // Ensure tiers are sorted by upperBound for reliable tier detection
  const sortedPricingTiers = useMemo(
    () =>
      [...(currentPlan?.pricing ?? [])].toSorted(
        (a, b) => a.upperBound - b.upperBound,
      ),
    [currentPlan?.pricing],
  )

  // Use the discounts data from queryData
  const discounts = useMemo(
    () => queryData?.discounts || [],
    [queryData?.discounts],
  )

  // Calculate the base price using tiered pricing
  const baseCalculation = useMemo(
    () => calculateTierPrice(sortedPricingTiers, safeChannelCount),
    [sortedPricingTiers, safeChannelCount],
  )

  // Apply discounts to get the final calculation
  const discountedCalculation = useMemo(
    () => applyDiscountsToCalculation(baseCalculation, discounts),
    [baseCalculation, discounts],
  )

  const volumeDiscountMessage = getVolumeDiscountMessage(
    safeChannelCount,
    sortedPricingTiers,
  )

  const { discountBadgeCopy, discountDescription } = getDiscountCopy(
    webBilling.discounts,
  )

  const currentlyPaying = hasUpcomingInvoice
    ? formatPriceSimple(upcomingInvoiceAmountRaw, currency)
    : formatPriceSimple(0, currency)

  // Get the total price with discounts applied
  const totalFuturePlanPrice = discountedCalculation.totalPrice
  const dueNowAmount = dueNow ?? 0

  const totalDueAmountWithCurrency = formatPrice(totalFuturePlanPrice, currency)

  const totalDueNowAmountWithCurrency = formatPrice(dueNowAmount, currency)

  const totalDueHeader = `Total: ${totalDueAmountWithCurrency}/${subscription.interval}`

  const totalDueSubHeader = isDowngradingAction
    ? `${totalDueAmountWithCurrency} will be billed on ${customInvoiceData?.nextBillingDate}`
    : `${totalDueNowAmountWithCurrency} due now · Billing is prorated for mid-cycle changes `

  const ctaButtonText = isDowngradingAction
    ? 'Confirm Plan Change'
    : 'Confirm and Pay'

  const fetchPricing = useCallback(
    async (quantity: number): Promise<void> => {
      if (!queryData?.subscription) return

      try {
        await getCustomInvoice({
          organizationId: organization?.id || '',
          planId: queryData.subscription.plan.id,
          interval: queryData.subscription.interval,
          quantity,
        })
      } catch {
        // Silent catch for graceful degradation
      }
    },
    [getCustomInvoice, organization?.id, queryData?.subscription],
  )

  const showAddOrRemoveChannelsButton =
    !isOnFreePlan &&
    !isPayingWithBank &&
    !webBilling.requiresAssistedBilling &&
    !isOnTrial &&
    hasPaymentDetails &&
    !manageChannelsExpanded

  const handleUnlockChannels = useCallback(() => {
    if (isOnFreePlan || !showAddOrRemoveChannelsButton) {
      openGlobalModal(MODALS.planSelector, {
        cta: 'billing-plansCard-unlock-1',
        upgradePathName: 'account-upgrade',
      })
    } else {
      setManageChannelsExpanded(true)
    }
  }, [isOnFreePlan, showAddOrRemoveChannelsButton, openGlobalModal])

  const handleCountChange = useCallback(
    (value: number) => {
      setChannelCount(value)
      void fetchPricing(value)
    },
    [fetchPricing],
  )

  const updateType =
    safeChannelCount > subscription.quantity
      ? UpdateType.ChannelIncrease
      : UpdateType.ChannelDecrease

  const handleConfirmAndPayClick = async (): Promise<void> => {
    try {
      const planId = subscription.plan.id
      if (!isObPlanId(planId) || !isBillingInterval(subscription.interval)) {
        return
      }

      const currentParams = {
        plan: planId,
        interval: subscription.interval,
        quantity: safeChannelCount,
        // Attribute the plan change to the billing page plan cards so the
        // subscription update carries marketing attribution downstream.
        attribution: {
          cta: 'billing-plansCard-confirmAndPay-1',
          upgradePathName: 'account-upgrade',
        },
      }

      // SCENARIO A: Pure retry - reuse client secret (VLG-394)
      const retryCheckParams = {
        planId: currentParams.plan,
        interval: currentParams.interval,
        quantity: currentParams.quantity,
      }

      if (
        isScaEnabled &&
        canRetry() &&
        !hasParametersChanged(retryCheckParams) &&
        retryState.session
      ) {
        setUpdatePlanPhase(UpdatePlanPhase.Authorizing)

        let confirmResult
        try {
          confirmResult = await confirmPayment(
            retryState.session.clientSecret,
            retryState.session.paymentMethodId ?? undefined,
          )
        } catch {
          setUpdatePlanPhase(UpdatePlanPhase.Idle)
          throw new Error('Payment authorization failed. Please try again.')
        }

        if (confirmResult.success) {
          // Success - clear retry state
          clearRetryData()

          setUpdatePlanPhase(UpdatePlanPhase.Confirming)
          await refetch()

          // Build success payload
          const subscriptionUpdate: SubscriptionUpdate = {
            updateType,
            planId,
            scheduledUpdate: null, // Immediate upgrade after SCA
          }

          // Clear updating state and show success
          setUpdatePlanPhase(UpdatePlanPhase.Idle)
          setSubscriptionUpdateData(subscriptionUpdate)
          setShowSuccessModal(true)
          setManageChannelsExpanded(false)
          setShowOverrideDialog(false)
          return
        } else {
          // Still failed - keep retry mode active (state unchanged)
          setUpdatePlanPhase(UpdatePlanPhase.Idle)
          throw new Error('Payment authorization failed. Please try again.')
        }
      }

      // SCENARIO B: New attempt or parameters changed (VLG-394)
      // Clear any existing retry state
      clearRetryData()

      // Set updating phase
      setUpdatePlanPhase(UpdatePlanPhase.Updating)

      const response = await updatePlan(currentParams)

      // Check if payment requires additional authentication (SCA)
      if (
        isScaEnabled &&
        response?.status === 'requiresAction' &&
        response?.paymentAction?.clientSecret
      ) {
        // Save retry session for potential retry (VLG-394)
        saveRetryData({
          clientSecret: response.paymentAction.clientSecret,
          paymentMethodId: response.paymentAction.paymentMethodId ?? null,
          planId: currentParams.plan,
          interval: currentParams.interval,
          quantity: currentParams.quantity,
        })

        // Set phase to authorizing before showing Stripe modal
        setUpdatePlanPhase(UpdatePlanPhase.Authorizing)

        // Trigger Stripe 3D Secure authentication
        let confirmResult
        try {
          confirmResult = await confirmPayment(
            response.paymentAction.clientSecret,
            response.paymentAction.paymentMethodId ?? undefined,
          )
        } catch {
          // Handle unexpected errors from confirmPayment (network failures, etc.)
          setUpdatePlanPhase(UpdatePlanPhase.Idle)
          // Retry mode already enabled by saveRetryData above
          throw new Error('Payment authorization failed. Please try again.')
        }

        if (confirmResult.success) {
          // Payment confirmation succeeded - Stripe automatically updates subscription
          // Clear retry state (VLG-394)
          clearRetryData()

          // Set phase to confirming to show feedback while we refetch
          setUpdatePlanPhase(UpdatePlanPhase.Confirming)

          // Refetch to get updated subscription data
          await refetch()

          // Build success payload
          const subscriptionUpdate: SubscriptionUpdate = {
            updateType,
            planId,
            scheduledUpdate: null, // Immediate upgrade after SCA
          }

          // Clear updating state and show success
          setUpdatePlanPhase(UpdatePlanPhase.Idle)
          setSubscriptionUpdateData(subscriptionUpdate)
          setShowSuccessModal(true)
          setManageChannelsExpanded(false)
          setShowOverrideDialog(false)
          return
        } else {
          // Payment confirmation failed or was canceled
          // Retry mode already enabled by saveRetryData above
          setUpdatePlanPhase(UpdatePlanPhase.Idle)
          throw new Error(
            'Payment authorization failed. Your subscription remains unchanged',
          )
        }
      }

      // Normal flow - no SCA required
      // Clear updating state
      setUpdatePlanPhase(UpdatePlanPhase.Idle)

      // Set the subscription update data for direct modal rendering
      setSubscriptionUpdateData({
        updateType,
        planId,
        scheduledUpdate: response?.scheduledUpdate || null,
      })
      setShowSuccessModal(true)

      setManageChannelsExpanded(false)
      setShowOverrideDialog(false)
    } catch {
      toast.error('Sorry, there was an error updating your subscription')
      setUpdatePlanPhase(UpdatePlanPhase.Idle)
      setShowOverrideDialog(false)
    }
  }

  return (
    <>
      <Flex direction="column" gap="md" fullWidth className={styles.container}>
        {/* Upcoming Bill Card */}
        <Card className={clsx(styles.card)}>
          <div className={styles.iconWrapper}>
            <ShoppingCartIcon />
          </div>
          <Flex direction="column" gap="2xs">
            <Heading
              as="h3"
              size="xsmall"
              iconPosition="end"
              icon={
                isOnTrial ? undefined : (
                  <Tooltip
                    content="This amount could change if adjustments are made mid-billing cycle to your number of connected channels. We'll always account for those changes and include the details in your invoice."
                    side="bottom"
                  >
                    <Text color="subtle" className={styles.usageChanges}>
                      <InfoIcon size="small" />
                    </Text>
                  </Tooltip>
                )
              }
            >
              {isOnTrial ? 'First Bill After Trial' : 'Upcoming Bill'}
            </Heading>
            {!isOnTrial ? (
              <Flex className={styles.upcomingBillCopy}>
                <Text weight="bold">
                  {/* Use upcoming invoice amount, or fallback to calculated price */}
                  {currentlyPaying}
                </Text>
                {/* Only show billing cycle if there's no scheduled plan change */}
                {!displayScheduledUpdate && (
                  <>
                    <span className={styles.separator}>·</span>
                    <Text>
                      Billed{' '}
                      {subscription.interval === 'year' ? 'yearly' : 'monthly'}
                    </Text>
                  </>
                )}
                <span className={styles.separator}>·</span>
                <Text>
                  Renews on{' '}
                  <Text weight="bold">
                    {subscription.periodEnd
                      ? new Date(subscription.periodEnd).toLocaleDateString(
                          'en-US',
                          {
                            month: 'long',
                            day: 'numeric',
                            year: 'numeric',
                          },
                        )
                      : 'N/A'}
                  </Text>
                </Text>
              </Flex>
            ) : (
              <>
                {!hasPaymentDetails ? (
                  <Text color="subtle">
                    You won&apos;t be charged unless you choose to subscribe
                  </Text>
                ) : (
                  <Flex className={styles.upcomingBillCopy}>
                    <Text weight="bold">{currentlyPaying}</Text>
                    {/* Only show billing cycle if there's no scheduled plan change */}
                    {!displayScheduledUpdate && (
                      <>
                        <span className={styles.separator}>·</span>
                        <Text>
                          Billed{' '}
                          {subscription.interval === 'year'
                            ? 'yearly'
                            : 'monthly'}
                        </Text>
                      </>
                    )}
                    <span className={styles.separator}>·</span>
                    <Text>
                      Starts on{' '}
                      <Text weight="bold">
                        {subscription.trial?.endDate
                          ? new Date(
                              subscription.trial.endDate,
                            ).toLocaleDateString('en-US', {
                              month: 'long',
                              day: 'numeric',
                              year: 'numeric',
                            })
                          : 'N/A'}
                      </Text>
                    </Text>
                  </Flex>
                )}
              </>
            )}
            {discountDescription && (
              <Text
                size="sm"
                className={styles.discountContainer}
                icon={<CircleDollarSignIcon size="xsmall" />}
              >
                {discountDescription} discount applied
              </Text>
            )}
          </Flex>
        </Card>

        {/* Channels Card */}
        <Card className={styles.card}>
          <Flex direction="column" gap="sm" fullWidth>
            <Flex
              direction="row"
              align="center"
              justify="between"
              fullWidth
              className={styles.channelsContainer}
            >
              <Flex direction="row" gap="sm" align="start">
                <div className={styles.iconWrapper}>
                  <AllChannelsIcon />
                </div>
                <Flex direction="column" gap="2xs">
                  <Heading as="h3" size="xsmall">
                    Channels
                  </Heading>
                  <Flex
                    direction="row"
                    gap="xs"
                    className={styles.channelsCopy}
                  >
                    <Text color="subtle">
                      {isOnTrial
                        ? 'Unlimited channels'
                        : channelsLimit != null
                          ? `${connectedChannelsCount}/${channelsLimit} ${
                              channelsLimit === 1 ? 'channel' : 'channels'
                            } connected`
                          : `${connectedChannelsCount} ${
                              connectedChannelsCount === 1
                                ? 'channel'
                                : 'channels'
                            } connected`}
                    </Text>
                  </Flex>
                  {/* Locked channels warning under the left column */}
                  {lockedChannelsCount > 0 && (
                    <Flex direction="row" gap="xs" align="center" wrap="wrap">
                      <Text
                        size="sm"
                        color="critical"
                        icon={<CriticalIcon color="critical" size="xsmall" />}
                      >
                        {lockedChannelsCount} locked
                      </Text>
                      {!isPayingWithBank &&
                        !webBilling.requiresAssistedBilling &&
                        !manageChannelsExpanded && (
                          <Button
                            type="button"
                            variant="secondary"
                            size="small"
                            onClick={handleUnlockChannels}
                          >
                            <LockOpenIcon /> Unlock Channels
                          </Button>
                        )}
                    </Flex>
                  )}
                </Flex>
              </Flex>

              {/* Right column - Add/Remove button */}
              {showAddOrRemoveChannelsButton && (
                <Button
                  size="small"
                  variant="tertiary"
                  onClick={() => {
                    // Agency plans require dialog for 10-channel minimum enforcement
                    if (isAgencyPlan) {
                      setShowAddOrRemoveChannelsDialog(true)
                    } else {
                      setManageChannelsExpanded(true)
                    }
                  }}
                  className={styles.addRemoveChannelsButton}
                >
                  Add/Remove Channels
                </Button>
              )}
            </Flex>

            {/* Manage channels UI */}
            {manageChannelsExpanded && !webBilling.requiresAssistedBilling && (
              <>
                <Card className={styles.manageChannels}>
                  <CounterInput
                    value={safeChannelCount}
                    onChange={handleCountChange}
                    name="channelCount"
                    min={CHANNEL_LIMITS.min}
                    disabled={false}
                    size="medium"
                    aria-label="Number of channels"
                    step={1}
                    className={styles.channelSelector}
                    style={getInputWidthStyle(safeChannelCount)}
                    suffix={
                      <Text size="md">
                        {safeChannelCount === 1 ? 'channel' : 'channels'}
                      </Text>
                    }
                  />
                  <Flex direction="column" gap="xs" align="end">
                    <Flex direction="row" gap="xs" align="center">
                      <Text size="md" weight="bold">
                        {totalDueHeader}
                      </Text>
                      <Text size="sm" color="subtle">
                        + applicable taxes
                      </Text>
                    </Flex>

                    {(volumeDiscountMessage || discountBadgeCopy) && (
                      <Flex direction="row" gap="2xs" align="end">
                        {volumeDiscountMessage &&
                          sortedPricingTiers.length > 2 && (
                            <Badge variant="brand-subtle">
                              {volumeDiscountMessage}
                            </Badge>
                          )}
                        {discountBadgeCopy && (
                          <Badge variant="brand-subtle">
                            {discountBadgeCopy}
                          </Badge>
                        )}
                      </Flex>
                    )}
                    {hasChannelCountChanged && (
                      <Skeleton
                        show={
                          isLoadingDynamicPrices ||
                          customInvoiceData === undefined
                        }
                      >
                        <Text size="sm" color="subtle">
                          {totalDueSubHeader}
                        </Text>
                      </Skeleton>
                    )}
                  </Flex>
                </Card>

                <Flex direction="row" gap="xs" align="center" fullWidth>
                  <Text
                    size="sm"
                    className={styles.paymentCopy}
                    icon={<CheckIcon />}
                  >
                    {isDowngradingAction
                      ? 'Changes take effect at your next billing date.'
                      : 'Changes take effect immediately after payment.'}
                  </Text>
                  <Button
                    size="large"
                    variant="secondary"
                    onClick={() => setManageChannelsExpanded(false)}
                    disabled={isUpdatingPlan || isConfirming}
                  >
                    Cancel
                  </Button>
                  {displayScheduledUpdate ||
                  subscription.isCanceledAtPeriodEnd ? (
                    <PlanOverrideConfirmationDialog
                      subscription={subscription}
                      onConfirm={handleConfirmAndPayClick}
                      loading={isUpdatingPlan}
                      open={showOverrideDialog}
                      onOpenChange={setShowOverrideDialog}
                    >
                      <Button
                        size="large"
                        variant="primary"
                        disabled={
                          !hasChannelCountChanged ||
                          isUpdatingPlan ||
                          isConfirming
                        }
                        loading={isUpdatingPlan || isConfirming}
                        onClick={() => setShowOverrideDialog(true)}
                      >
                        {isUpdatingPlan || isConfirming
                          ? updatePlanPhase === UpdatePlanPhase.Updating
                            ? 'Updating plan'
                            : updatePlanPhase === UpdatePlanPhase.Authorizing
                              ? 'Authorizing payment'
                              : updatePlanPhase === UpdatePlanPhase.Confirming
                                ? 'Confirming payment'
                                : ctaButtonText
                          : ctaButtonText}
                      </Button>
                    </PlanOverrideConfirmationDialog>
                  ) : (
                    <Button
                      size="large"
                      variant="primary"
                      disabled={
                        !hasChannelCountChanged ||
                        isUpdatingPlan ||
                        isConfirming
                      }
                      loading={isUpdatingPlan || isConfirming}
                      onClick={handleConfirmAndPayClick}
                    >
                      {isUpdatingPlan || isConfirming
                        ? updatePlanPhase === UpdatePlanPhase.Updating
                          ? 'Updating plan'
                          : updatePlanPhase === UpdatePlanPhase.Authorizing
                            ? 'Authorizing payment'
                            : updatePlanPhase === UpdatePlanPhase.Confirming
                              ? 'Confirming payment'
                              : ctaButtonText
                        : ctaButtonText}
                    </Button>
                  )}
                </Flex>
              </>
            )}
          </Flex>
        </Card>

        {/* Users Card */}
        <Card className={styles.card}>
          <Flex direction="row" align="center" justify="between" fullWidth>
            <Flex direction="row" gap="sm" align="start">
              <div className={styles.iconWrapper}>
                <UsersIcon />
              </div>
              <Flex direction="column" gap="2xs">
                <Heading as="h3" size="xsmall">
                  Users
                </Heading>
                <Text color="subtle">{usersCopy}</Text>
              </Flex>
            </Flex>
          </Flex>
        </Card>
      </Flex>

      {/* Success Modal - Direct Rendering */}
      {subscriptionUpdateData && (
        <SubscriptionUpdateModal
          open={showSuccessModal}
          onOpenChange={(open) => {
            setShowSuccessModal(open)
            if (!open) {
              setSubscriptionUpdateData(null)
            }
          }}
          subscriptionUpdate={subscriptionUpdateData}
        />
      )}

      {/* Add/Remove Channels Dialog - Used for agency plans */}
      <AddOrRemoveChannelsDialog
        open={showAddOrRemoveChannelsDialog}
        onOpenChange={setShowAddOrRemoveChannelsDialog}
        cta="billing-plansCard-addRemoveChannels-1"
      />
    </>
  )
}

export default PlanCardsVertical
